/* custom-carousel.js — shared behaviors for the Hamilton custom sections.
   Two modes, selected by data attribute on the section root:
     [data-custom-slideshow]  fade slideshow (hero, brand-banner) — uses
                           [data-custom-slide] children + prerendered .custom-dots__dot
     [data-custom-carousel]   scroll-snap carousel (featured, brand-tiles) — uses
                           [data-custom-track] + [data-custom-dots] (dots generated)
   Vanilla JS, no dependencies. Safe to init multiple times (editor reloads). */
(function () {
  /* ---- fade slideshow ---- */
  class Slideshow {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-custom-slide]'));
      this.dots = Array.from(root.querySelectorAll('.custom-dots__dot'));
      this.i = 0;
      this.n = this.slides.length;
      this.autoplay = root.dataset.autoplay === 'true';
      this.speed = (parseInt(root.dataset.speed, 10) || 5) * 1000;
      this.timer = null;
      this.stage = root.querySelector('.custom-slidestage');
      this.prevBtns = Array.from(root.querySelectorAll('[data-custom-prev]'));
      this.nextBtns = Array.from(root.querySelectorAll('[data-custom-next]'));
      if (this.n <= 1) return;
      if (this.stage) this.stage.setAttribute('data-ready', '');
      /* Accessibility (18 Oct 2026): announce as a carousel, honour
         prefers-reduced-motion, give rotating slideshows a pause/play
         control (WCAG 2.2.2), pause while keyboard focus is inside, and make
         hidden slides inert so their links aren't tabbable. */
      root.setAttribute('role', 'region');
      root.setAttribute('aria-roledescription', 'carousel');
      if (!root.hasAttribute('aria-label')) root.setAttribute('aria-label', 'Featured');
      this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.reduced) this.autoplay = false;
      this.userPaused = false;
      this.focusPaused = false;
      if (this.autoplay) this.addToggle();
      root.addEventListener('focusin', (e) => {
        if (e.target.closest && e.target.closest('.custom-dots__toggle')) return;
        this.focusPaused = true; this.stop();
      });
      root.addEventListener('focusout', (e) => {
        if (!root.contains(e.relatedTarget)) { this.focusPaused = false; this.start(); }
      });
      this.go(0);
      this.dots.forEach((d, k) => d.addEventListener('click', () => { this.go(k); this.restart(); }));
      this.prevBtns.forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
        this.restart();
      }));
      this.nextBtns.forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
        this.restart();
      }));
      this.start();
      /* Autoplay used to pause on mouseenter and resume on mouseleave. On the
         home hero the cursor sits over the slideshow most of the time, so the
         pause read as "autoplay doesn't work". Keep rotating under the cursor;
         only pause while the tab is in the background (a timer that keeps
         firing in a hidden tab would skip ahead on return). */
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop(); else this.start();
      });
    }
    addToggle() {
      const wrap = this.root.querySelector('.custom-dots');
      if (!wrap) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'custom-dots__toggle';
      this.toggleBtn = b;
      this.renderToggle();
      b.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (this.userPaused) this.stop(); else this.start();
        this.renderToggle();
      });
      wrap.appendChild(b);
    }
    renderToggle() {
      const b = this.toggleBtn;
      if (!b) return;
      const paused = this.userPaused;
      b.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      b.setAttribute('aria-pressed', paused ? 'true' : 'false');
      b.innerHTML = paused
        ? '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M2 1l7 4-7 4z"/></svg>'
        : '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M2 1h2v8H2zM6 1h2v8H6z"/></svg>';
    }
    go(k) {
      this.i = (k + this.n) % this.n;
      this.slides.forEach((s, m) => {
        const on = m === this.i;
        s.classList.toggle('is-active', on);
        if (on) { s.removeAttribute('inert'); s.removeAttribute('aria-hidden'); }
        else { s.setAttribute('inert', ''); s.setAttribute('aria-hidden', 'true'); }
      });
      this.dots.forEach((d, m) => {
        d.classList.toggle('is-active', m === this.i);
        if (m === this.i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
    }
    next() { this.go(this.i + 1); }
    prev() { this.go(this.i - 1); }
    start() {
      if (!this.autoplay || this.userPaused || this.focusPaused || this.timer) return;
      this.timer = setInterval(() => this.next(), this.speed);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() { this.stop(); this.start(); }
  }

  /* ---- scroll-snap carousel with dynamic dots ---- */
  class Carousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-custom-track]');
      this.dotsWrap = root.querySelector('[data-custom-dots]');
      if (!this.track || !this.dotsWrap) return;
      this.build = this.build.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.build();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('resize', this.build);
      /* Mouse affordances (client feedback 16 Oct 2026: the carousel only
         moved by swipe). Prev/next arrows where the section renders them,
         and click-and-drag on the track for a mouse pointer. */
      this.prevBtns = Array.from(root.querySelectorAll('[data-custom-prev]'));
      this.nextBtns = Array.from(root.querySelectorAll('[data-custom-next]'));
      this.prevBtns.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.page(-1); }));
      this.nextBtns.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.page(1); }));
      this.initDrag();
    }
    /** Scroll one viewport-width of cards; wraps at either end. */
    page(dir) {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      let left = scrollLeft + dir * clientWidth;
      if (dir > 0 && scrollLeft >= max - 1) left = 0;
      else if (dir < 0 && scrollLeft <= 1) left = max;
      this.track.scrollTo({ left, behavior: 'smooth' });
    }
    /** Show the arrows only while the track actually overflows. */
    updateArrows() {
      const canScroll = this.track.scrollWidth > this.track.clientWidth + 1;
      this.prevBtns.concat(this.nextBtns).forEach((b) => { b.hidden = !canScroll; });
    }
    /**
     * Click-and-drag scrolling for mouse users. Pen/touch keep the browser's
     * native panning. A real drag (more than 6px) swallows the click that
     * would otherwise fire on the card under the pointer when the button is
     * released. Snap and smooth scrolling are suspended during the drag so
     * the track follows the pointer 1:1, then restored so it settles on a
     * card.
     */
    initDrag() {
      const t = this.track;
      let startX = 0, startLeft = 0, dragging = false, moved = false;
      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (t.scrollWidth <= t.clientWidth) return;
        dragging = true; moved = false;
        startX = e.clientX; startLeft = t.scrollLeft;
        t.style.scrollSnapType = 'none';
        t.style.scrollBehavior = 'auto';
        t.classList.add('is-dragging');
      });
      t.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; t.setPointerCapture(e.pointerId); }
        if (moved) { e.preventDefault(); t.scrollLeft = startLeft - dx; }
      });
      const end = (e) => {
        if (!dragging) return;
        dragging = false;
        t.classList.remove('is-dragging');
        if (e && e.pointerId !== undefined && t.hasPointerCapture && t.hasPointerCapture(e.pointerId)) {
          t.releasePointerCapture(e.pointerId);
        }
        t.style.scrollSnapType = '';
        t.style.scrollBehavior = '';
        if (moved) {
          /* nudge so the restored snap settles on the nearest card */
          t.scrollTo({ left: t.scrollLeft, behavior: 'smooth' });
        }
      };
      t.addEventListener('pointerup', end);
      t.addEventListener('pointercancel', end);
      t.addEventListener('lostpointercapture', end);
      t.addEventListener('click', (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
      t.addEventListener('dragstart', (e) => { if (dragging) e.preventDefault(); });
    }
    pages() {
      const { scrollWidth, clientWidth } = this.track;
      if (!clientWidth) return 1;
      return Math.max(1, Math.round(scrollWidth / clientWidth));
    }
    build() {
      if (this.prevBtns) this.updateArrows();
      const p = this.pages();
      this.dotsWrap.innerHTML = '';
      if (p <= 1) { this.dotsWrap.style.display = 'none'; return; }
      this.dotsWrap.style.display = '';
      for (let i = 0; i < p; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'custom-dots__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to group ' + (i + 1));
        dot.addEventListener('click', () =>
          this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' }));
        this.dotsWrap.appendChild(dot);
      }
    }
    onScroll() {
      const a = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this.dotsWrap.querySelectorAll('.custom-dots__dot').forEach((d, i) =>
        d.classList.toggle('is-active', i === a));
    }
    /**
     * Redirects a normal vertical wheel/trackpad scroll into horizontal
     * carousel movement while the pointer is over the track — without
     * this, moving the carousel by scroll requires holding shift (the
     * browser's native modifier for turning vertical wheel into
     * horizontal scroll). Releases back to normal page scrolling once
     * the track hits either end, or for gestures that are already
     * horizontal (trackpad swipes), so it never traps the page scroll.
     * @param {WheelEvent} e
     */
    onWheel(e) {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (scrollWidth <= clientWidth) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      e.preventDefault();
      /* Firefox reports mouse-wheel deltas in lines (deltaMode 1, ~3 per
         notch) rather than pixels, which moved the track only a few pixels
         per click; convert line and page deltas to pixels first. */
      let delta = e.deltaY;
      if (e.deltaMode === 1) delta *= 40;
      else if (e.deltaMode === 2) delta *= clientWidth;
      /* scroll-behavior: smooth on the track made each wheel notch animate
         from wherever the previous animation had got to, so fast wheeling
         crawled; apply wheel movement instantly and let snap settle it. */
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft += delta;
      this.track.style.scrollBehavior = '';
    }
  }

  function init() {
    document.querySelectorAll('[data-custom-slideshow]').forEach((el) => {
      if (!el.dataset.customInit) { el.dataset.customInit = '1'; new Slideshow(el); }
    });
    document.querySelectorAll('[data-custom-carousel]').forEach((el) => {
      if (!el.dataset.customInit) { el.dataset.customInit = '1'; new Carousel(el); }
    });
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
